import { Role } from "src/auth/types/interfaces";
import { ISO_COUNTRY_CODES } from "src/app/constants";
import { SEDGWICK, SEDGWICK_CANADA } from "src/forms/constants";
import { FeatureFlags } from "src/app/featureFlags";
/**
 * Normalizes a country code to its ISO 3166-1 alpha-2 form.
 * Handles the legacy "CAN" format for backward compatibility.
 */
const normalizeCountry = (country: string): string =>
  country.toUpperCase() === "CAN"
    ? ISO_COUNTRY_CODES.CANADA
    : country.toUpperCase();

/**
 * Centralized WC country configuration.
 *
 * Every country-specific behavior in the WC flow should be driven by this
 * config. To add a new country:
 *   1. Add the country code to ISO_COUNTRY_CODES in src/app/constants.ts
 *   2. Add a Role entry in src/auth/types/interfaces.ts
 *   3. Add an entry here
 *   4. Create a TPA override file if the country needs custom form fields
 *
 * Consumers should use getWcCountryConfig() or getWcCountryConfigByTpa()
 * to look up config instead of writing if/else country branches.
 */

export interface WcCountryAccessDeniedModal {
  title: string;
  description: string;
}

export interface WcCountryConfig {
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Display label for UI (e.g. "Canada", "United States") */
  label: string;
  /** Roles that grant access to submit claims for this country */
  roles: Role[];
  /** TPA identifier used when the incident site is in this country */
  tpaIdentifier: string;
  /**
   * Feature flag gate — evaluated lazily to avoid module-load-time
   * side effects. Returns false to disable this country's WC flow.
   * Defaults to () => true if not provided.
   */
  isEnabled: () => boolean;
  /** Modal content shown when a user lacks the required role */
  accessDeniedModal: WcCountryAccessDeniedModal;
  /**
   * URL for file attachment overflow tickets (videos, oversized files).
   * If set, the file upload alert links to this URL instead of the
   * default SIM ticket generator. Each country can define its own
   * ticketing system (e.g. RIVER for Canada).
   */
  fileAttachmentTicketUrl?: string;
  /**
   * Base URL for the WC claim referral workflow (e.g. RIVER for Canada).
   * Used in the "site not found" error to direct users to submit claims
   * manually when their site is not yet in Nucleus.
   */
  wcClaimReferralUrl?: string;
  /**
   * URL template for reporting a missing site via SIM ticket.
   * Used in the "site not found" error to let users report their site
   * to the Nucleus Tech team.
   */
  missingSiteTicketUrl?: string;
}

// FeatureFlags is imported as an ES module at the top of the file. It
// resolves through Vite/Vitest's module graph (rather than Node's
// CommonJS resolver) so tsconfig path mappings like `src/...` are
// honored, and so test mocks via `vi.mock("src/app/featureFlags", ...)`
// correctly intercept reads from this module. The previous Jest-era
// lazy `require()` pattern is no longer needed under Vitest.
const getFeatureFlags = () => FeatureFlags;

/**
 * Registry of all WC country configurations.
 * Keyed by ISO 3166-1 alpha-2 country code.
 *
 * US does not have an entry here because US TPA resolution is state-based
 * (Sedgwick / Helmsman / Anchor) and handled separately in getTpaFromSiteAndDate.
 * US is treated as the default/fallback country.
 */
export const WC_COUNTRY_CONFIGS: Record<string, WcCountryConfig> = {
  [ISO_COUNTRY_CODES.CANADA]: {
    countryCode: ISO_COUNTRY_CODES.CANADA,
    label: "Canada",
    roles: [Role.WC_CLAIM_CANADA_USER],
    tpaIdentifier: SEDGWICK_CANADA,
    isEnabled: () => getFeatureFlags().wcCanadaForm,
    accessDeniedModal: {
      title: "Canada Claims Access Required",
      description:
        "You do not have permission to submit a claim for a Canada incident site.",
    },
    fileAttachmentTicketUrl:
      "https://river.amazon.com/workflow/VNC/run/wfss-86347615-1dd3-434b-b413-8aad3239d697",
    wcClaimReferralUrl:
      "https://river.amazon.com/workflow/VNC/run/wfss-86347615-1dd3-434b-b413-8aad3239d697/" +
      "?buildingType=GRMC&q0=f5d89525-e913-4363-a8c9-1a4c76c019cc&q1=&q2=&q3=&q4=",
    missingSiteTicketUrl:
      "https://t.corp.amazon.com/create/templates/9f174916-a8c2-4da8-af8e-45e5918e25e7",
  },
};

/**
 * US default config. Used as fallback when the incident site country
 * is US or not found in WC_COUNTRY_CONFIGS.
 */
export const WC_US_DEFAULT_CONFIG: WcCountryConfig = {
  countryCode: ISO_COUNTRY_CODES.UNITED_STATES,
  label: "United States",
  roles: [Role.WC_CLAIM_USER, Role.WC_CLAIM_USER_LIMITED],
  tpaIdentifier: SEDGWICK, // Default; actual TPA is resolved by state/date
  isEnabled: () => true,
  accessDeniedModal: {
    title: "US Claims Access Required",
    description:
      "You do not have permission to submit a claim for a US incident site.",
  },
};

/**
 * Look up WC country config by country code.
 * Returns the matching config, or the US default for unknown/US countries.
 */
export const getWcCountryConfig = (country?: string): WcCountryConfig => {
  if (!country) return WC_US_DEFAULT_CONFIG;
  const normalized = normalizeCountry(country);
  return WC_COUNTRY_CONFIGS[normalized] ?? WC_US_DEFAULT_CONFIG;
};

/**
 * Returns true if the given config represents a non-US country
 * (i.e. it exists in WC_COUNTRY_CONFIGS, not the US default fallback).
 */
export const isNonUsCountryConfig = (config: WcCountryConfig): boolean =>
  config.countryCode !== WC_US_DEFAULT_CONFIG.countryCode;

/**
 * Look up WC country config by TPA identifier.
 * Returns the matching config, or the US default if no match.
 */
export const getWcCountryConfigByTpa = (tpa?: string): WcCountryConfig => {
  if (!tpa) return WC_US_DEFAULT_CONFIG;
  const match = Object.values(WC_COUNTRY_CONFIGS).find(
    (config) => config.tpaIdentifier === tpa
  );
  return match ?? WC_US_DEFAULT_CONFIG;
};

/**
 * Pre-computed set of all WC roles across all country configs.
 * Avoids recomputing on every hasCountryAccess call.
 */
const ALL_WC_ROLES: ReadonlySet<Role> = new Set([
  ...Object.values(WC_COUNTRY_CONFIGS).flatMap((c) => c.roles),
  ...WC_US_DEFAULT_CONFIG.roles,
]);

/**
 * Check if a user's roles grant access to submit claims for a given country.
 * Returns true if:
 *   - The user has no WC roles at all (page-level access control handles that)
 *   - The country is unknown/missing (don't block before a site is selected)
 *   - The user has at least one of the required roles for the given country
 */
export const hasCountryAccess = (
  userRoles: Role[],
  country?: string
): boolean => {
  const hasAnyWcRole = userRoles.some((role) => ALL_WC_ROLES.has(role));
  // If user has no WC roles at all, don't block here — page loader handles access
  if (!hasAnyWcRole) {
    return true;
  }
  // Unknown or missing country — do not block
  if (!country) {
    return true;
  }
  const normalized = normalizeCountry(country);
  const isKnownCountry =
    normalized === WC_US_DEFAULT_CONFIG.countryCode ||
    !!WC_COUNTRY_CONFIGS[normalized];
  if (!isKnownCountry) {
    return true;
  }
  const config = getWcCountryConfig(country);
  return config.roles.some((role) => userRoles.includes(role));
};

/**
 * Returns all WC country configs (including US default) where the user
 * has at least one matching role. Useful for rendering role-aware UI
 * that needs to show content per country.
 */
export const getWcCountryConfigsForRoles = (
  userRoles: Role[]
): WcCountryConfig[] => {
  const allConfigs = [
    ...Object.values(WC_COUNTRY_CONFIGS),
    WC_US_DEFAULT_CONFIG,
  ];
  return allConfigs.filter((config) =>
    config.roles.some((role) => userRoles.includes(role))
  );
};
